/* =========================================================
   TFX Ingredients
   Objectif :
   - carte entière cliquable
   - hauteur verrouillée (ouverte = fermée)
   Rollback :
   - supprimer ce fichier + l’include
   ========================================================= */

const TFX_INGREDIENTS_LAYOUT_SINGLE = 'single-column';
const TFX_INGREDIENTS_LAYOUT_MULTI = 'multi-column';

const tfxIngredientsShared = (() => {
  function getCssNumberValue(value, fallback) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function hasIngredientSections(root) {
    const source = root || document;
    return !!(source && source.querySelector && source.querySelector('[data-tfx-ingredients]'));
  }

  function getIngredientSections(root) {
    const source = root || document;
    const sections = [];

    if (source.matches && source.matches('[data-tfx-ingredients]')) {
      sections.push(source);
    }

    if (source.querySelectorAll) {
      return sections.concat(Array.from(source.querySelectorAll('[data-tfx-ingredients]')));
    }

    return sections;
  }

  function resolveSectionLayout(section) {
    const grid = section && section.querySelector ? section.querySelector('.tfx-ingredients__grid') : null;
    if (!grid || !window.getComputedStyle) return TFX_INGREDIENTS_LAYOUT_SINGLE;

    const visibleItems = Array.from(grid.querySelectorAll('.tfx-ingredient')).filter((item) => {
      return !item.hidden && item.getClientRects && item.getClientRects().length;
    });

    if (visibleItems.length >= 2) {
      const firstRect = visibleItems[0].getBoundingClientRect();
      const secondRect = visibleItems[1].getBoundingClientRect();
      const sameRow = Math.abs(secondRect.top - firstRect.top) < 2;

      return sameRow ? TFX_INGREDIENTS_LAYOUT_MULTI : TFX_INGREDIENTS_LAYOUT_SINGLE;
    }

    const gridWidth = Math.floor(grid.getBoundingClientRect().width || 0);
    if (!gridWidth) {
      const currentLayout = section && section.getAttribute ? section.getAttribute('data-tfx-layout') : '';
      if (currentLayout === TFX_INGREDIENTS_LAYOUT_MULTI || currentLayout === TFX_INGREDIENTS_LAYOUT_SINGLE) {
        return currentLayout;
      }

      return TFX_INGREDIENTS_LAYOUT_SINGLE;
    }

    const sectionStyles = window.getComputedStyle(section);
    const gridStyles = window.getComputedStyle(grid);
    const cardMinInline = getCssNumberValue(sectionStyles.getPropertyValue('--tfx-card-min-inline'), 320);
    const columnGap = getCssNumberValue(gridStyles.columnGap || gridStyles.gap, 0);
    const minTwoColumnWidth = (cardMinInline * 2) + columnGap;

    return gridWidth + 0.5 < minTwoColumnWidth ? TFX_INGREDIENTS_LAYOUT_SINGLE : TFX_INGREDIENTS_LAYOUT_MULTI;
  }

  function syncSectionLayoutState(section) {
    const nextLayout = resolveSectionLayout(section);

    if (section && section.setAttribute) {
      section.setAttribute('data-tfx-layout', nextLayout);
    }

    return nextLayout;
  }

  function isMultiColumnLayout(section) {
    if (!section) return false;

    const layout = section.getAttribute('data-tfx-layout');
    if (layout === TFX_INGREDIENTS_LAYOUT_MULTI) return true;
    if (layout === TFX_INGREDIENTS_LAYOUT_SINGLE) return false;

    return resolveSectionLayout(section) === TFX_INGREDIENTS_LAYOUT_MULTI;
  }

  return {
    getIngredientSections,
    hasIngredientSections,
    isDesignMode,
    isMultiColumnLayout,
    syncSectionLayoutState
  };
})();

(function () {
  'use strict';

  const {
    hasIngredientSections,
    isDesignMode,
    isMultiColumnLayout,
    syncSectionLayoutState
  } = tfxIngredientsShared;

  if (!isDesignMode() && !hasIngredientSections(document)) {
    return;
  }

  function init(scope) {
    const root = scope || document;
    const sections = root.querySelectorAll('[data-tfx-ingredients]');

    sections.forEach(section => {
      const grid = section.querySelector('.tfx-ingredients__grid');
      if (!grid) return;
      syncSectionLayoutState(section);

      const allowMultiple = grid.dataset.allowMultiple === 'true';
      const items = grid.querySelectorAll('.tfx-ingredient');

      items.forEach(item => {
        if (item.dataset.tfxBound) return;
        item.dataset.tfxBound = 'true';

        const toggle = item.querySelector('[data-tfx-toggle]');
        const panel  = item.querySelector('[data-tfx-panel]');
        if (!toggle || !panel) return;

        /* BEGIN TFX PATCH: Ingredients toggle interaction hardening
           Objectif: éviter les conflits avec éléments interactifs internes (liens/champs/panel) et focus mort sur bouton décoratif.
           Rollback: supprimer ce bloc + helpers hasTextSelection/shouldIgnoreToggleFromTarget.
        */
        // Toggle button is decorative (click handled by card), avoid dead tab stop.
        toggle.setAttribute('tabindex', '-1');

        // Accessibilité clavier
        item.setAttribute('tabindex', '0');

        item.addEventListener('click', (e) => {
          if (shouldIgnoreToggleFromTarget(e.target, item)) return;
          if (hasTextSelection()) return;
          toggleItem(item, allowMultiple);
        });

        item.addEventListener('keydown', e => {
          // Do not hijack keyboard interaction from nested focusable elements.
          if (e.target !== item) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleItem(item, allowMultiple);
          }
        });
        /* END TFX PATCH */
      });
    });
  }

  function toggleItem(item, allowMultiple) {
    const isOpen = item.classList.contains('is-open');
    const grid = item.closest('.tfx-ingredients__grid');

    if (!allowMultiple && grid) {
      grid.querySelectorAll('.tfx-ingredient.is-open').forEach(el => {
        if (el !== item) close(el);
      });
    }

    isOpen ? close(item) : open(item);
  }

  /* BEGIN TFX PATCH: Ingredients toggle interaction hardening helpers
     Rollback: remove these helper functions and their callers in item click/keydown handlers.
  */
  function hasTextSelection() {
    if (!window.getSelection) return false;
    const selection = window.getSelection();
    return !!selection && selection.toString().trim().length > 0;
  }

  function shouldIgnoreToggleFromTarget(target, item) {
    if (!target || !(target instanceof Element)) return false;
    if (!item || !item.contains(target)) return true;

    return !!target.closest(
      'a, button, input, select, textarea, label, summary, [role="button"], [role="link"], [contenteditable="true"], [data-tfx-ignore-toggle], [data-tfx-panel]'
    );
  }
  /* END TFX PATCH */

  function lockHeight(item) {
    const section = item.closest('[data-tfx-ingredients]');
    if (!isMultiColumnLayout(section)) return;

    // Déjà verrouillée → ne rien refaire
    if (item.style.getPropertyValue('--tfx-locked-h')) return;

    const height = item.getBoundingClientRect().height;
    item.style.setProperty('--tfx-locked-h', `${height}px`);
  }

  function open(item) {
    const toggle = item.querySelector('[data-tfx-toggle]');
    const panel  = item.querySelector('[data-tfx-panel]');
    if (!toggle || !panel) return;

    // 1) Verrouille la hauteur AVANT ouverture
    lockHeight(item);

    toggle.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    item.classList.add('is-open');
  }

  function close(item) {
    const toggle = item.querySelector('[data-tfx-toggle]');
    const panel  = item.querySelector('[data-tfx-panel]');
    if (!toggle || !panel) return;

    toggle.setAttribute('aria-expanded', 'false');
    panel.hidden = true;
    item.classList.remove('is-open');
    // 👉 on garde la hauteur verrouillée pour éviter tout jump
  }

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:select', e => init(e.target));

})();

/* BEGIN TFX PATCH: Ingredients - see more uses section setting max_initial
   Rollback: restore previous CSS nth-child hiding rules + overflow detection.
*/
(function(){
  const RESIZE_BIND_KEY = 'tfxIngredientsSeeMoreResizeBound';
  const RESIZE_DEBOUNCE_MS = 140;
  const INITIAL_MIN = 1;
  const INITIAL_MAX = 24;
  const INITIAL_DEFAULT = 8;
  let resizeTimer = null;
  let rafId = null;

  const {
    getIngredientSections,
    hasIngredientSections,
    isDesignMode,
    syncSectionLayoutState
  } = tfxIngredientsShared;

  if (!isDesignMode() && !hasIngredientSections(document)) return;

  function clampInitial(value) {
    return Math.max(INITIAL_MIN, Math.min(INITIAL_MAX, value));
  }

  function parseInitialLimits(section) {
    const grid = section.querySelector('.tfx-ingredients__grid');
    const rawDesktop = grid ? Number.parseInt(grid.dataset.maxInitial || '', 10) : NaN;
    const desktop = Number.isFinite(rawDesktop) ? clampInitial(rawDesktop) : INITIAL_DEFAULT;

    const rawMobile = grid ? Number.parseInt(grid.dataset.maxInitialMobile || '', 10) : NaN;
    const mobile = Number.isFinite(rawMobile) ? clampInitial(rawMobile) : desktop;

    return { desktop, mobile };
  }

  function resolveMaxInitial(section) {
    const limits = parseInitialLimits(section);
    return syncSectionLayoutState(section) === TFX_INGREDIENTS_LAYOUT_SINGLE ? limits.mobile : limits.desktop;
  }

  function updateMoreButton(btn, totalItems, expanded) {
    const collapsedLabel = btn.getAttribute('data-tfx-more-label') || `VOIR LES ${totalItems} INGRÉDIENTS`;
    const expandedLabel = btn.getAttribute('data-tfx-less-label') || 'VOIR MOINS';

    btn.textContent = expanded ? expandedLabel : collapsedLabel;
    btn.setAttribute('aria-expanded', String(expanded));
  }

  function applyCollapsedState(section, items, maxInitial, expanded) {
    section.classList.toggle('is-expanded', expanded);

    items.forEach((item, index) => {
      const shouldHide = !expanded && index >= maxInitial;
      item.hidden = shouldHide;
    });
  }

  function initIngredientsSeeMore(root){
    const sections = getIngredientSections(root);
    if (!sections.length) return;

    sections.forEach((section) => {
      const items = Array.from(section.querySelectorAll('.tfx-ingredient'));
      const moreWrap = section.querySelector('.tfx-ingredients__more');
      const btn = section.querySelector('.tfx-ingredients__more-btn');
      const maxInitial = resolveMaxInitial(section);

      if (!moreWrap || !btn) return;
      if (!items.length) {
        moreWrap.setAttribute('hidden', '');
        return;
      }

      if (items.length <= maxInitial) {
        section.classList.remove('is-expanded');
        items.forEach((item) => {
          item.hidden = false;
        });
        moreWrap.setAttribute('hidden', '');
        updateMoreButton(btn, items.length, false);
        return;
      }

      moreWrap.removeAttribute('hidden');
      const expanded = section.classList.contains('is-expanded');
      applyCollapsedState(section, items, maxInitial, expanded);
      updateMoreButton(btn, items.length, expanded);

      if (btn.dataset.tfxBound) return;
      btn.dataset.tfxBound = 'true';

      btn.addEventListener('click', () => {
        const currentItems = Array.from(section.querySelectorAll('.tfx-ingredient'));
        const currentMaxInitial = resolveMaxInitial(section);
        const nextExpanded = !section.classList.contains('is-expanded');
        applyCollapsedState(section, currentItems, currentMaxInitial, nextExpanded);
        updateMoreButton(btn, currentItems.length, nextExpanded);
      });
    });
  }

  function scheduleInitIngredientsSeeMore() {
    if (resizeTimer) window.clearTimeout(resizeTimer);

    resizeTimer = window.setTimeout(() => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => initIngredientsSeeMore(document));
    }, RESIZE_DEBOUNCE_MS);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initIngredientsSeeMore(document);
  });

  document.addEventListener('shopify:section:load', (e) => {
    initIngredientsSeeMore(e.target);
  });

  document.addEventListener('shopify:section:select', (e) => {
    initIngredientsSeeMore(e.target);
  });

  if (!window[RESIZE_BIND_KEY]) {
    window[RESIZE_BIND_KEY] = true;
    window.addEventListener('resize', scheduleInitIngredientsSeeMore, { passive: true });
    window.addEventListener('orientationchange', scheduleInitIngredientsSeeMore, { passive: true });
    document.addEventListener('shopify:section:reorder', scheduleInitIngredientsSeeMore);
    document.addEventListener('shopify:block:select', scheduleInitIngredientsSeeMore);
    document.addEventListener('shopify:block:deselect', scheduleInitIngredientsSeeMore);
  }
})();
/* END TFX PATCH */

/* BEGIN TFX PATCH: Ingredients - manual hyphenation (words >= 10 chars only)
   Objective: keep justified text while restricting hyphenation to long words only.
   Scope: closed excerpt + opened richtext inside [data-tfx-ingredients].
   Rollback: remove this IIFE block and restore CSS hyphens:auto.
*/
(function () {
  'use strict';

  const BIND_KEY = 'tfxIngredientsManualHyphenationBound';
  const MIN_WORD_LENGTH = 10;
  const SOFT_HYPHEN = '\u00AD';
  const FALLBACK_WORD_REGEX = /([A-Za-zÀ-ÖØ-öø-ÿ]{10,})/g;
  let unicodeWordRegex = null;

  const { getIngredientSections, hasIngredientSections, isDesignMode } = tfxIngredientsShared;

  if (!isDesignMode() && !hasIngredientSections(document)) return;
  if (window[BIND_KEY]) return;
  window[BIND_KEY] = true;

  try {
    unicodeWordRegex = new RegExp('([\\p{L}\\p{M}]{' + MIN_WORD_LENGTH + ',})', 'gu');
  } catch (error) {
    unicodeWordRegex = null;
  }

  function shouldSkipTextNode(node) {
    if (!node || !node.nodeValue || !node.nodeValue.trim()) return true;
    if (!node.parentElement) return true;

    const parent = node.parentElement;
    if (parent.closest('[data-tfx-ignore-hyphenation]')) return true;

    return parent.matches('script, style, noscript, code, pre');
  }

  function addSoftHyphensToWord(word) {
    const cleanWord = word.replace(/\u00AD/g, '');
    if (cleanWord.length < MIN_WORD_LENGTH) return cleanWord;

    let hyphenated = '';
    for (let index = 0; index < cleanWord.length; index += 1) {
      hyphenated += cleanWord.charAt(index);

      const lettersBefore = index + 1;
      const lettersAfter = cleanWord.length - lettersBefore;
      const canInsertBreak = lettersBefore >= 5 && lettersAfter >= 4 && (lettersBefore - 5) % 4 === 0;

      if (canInsertBreak) {
        hyphenated += SOFT_HYPHEN;
      }
    }

    return hyphenated;
  }

  function hyphenateTextValue(value) {
    const sourceText = value.replace(/\u00AD/g, '');
    const regex = unicodeWordRegex || FALLBACK_WORD_REGEX;
    return sourceText.replace(regex, addSoftHyphensToWord);
  }

  function hyphenateContainer(container) {
    if (!container || !document.createTreeWalker) return;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let currentNode = walker.nextNode();

    while (currentNode) {
      textNodes.push(currentNode);
      currentNode = walker.nextNode();
    }

    textNodes.forEach((textNode) => {
      if (shouldSkipTextNode(textNode)) return;
      const nextValue = hyphenateTextValue(textNode.nodeValue || '');
      if (nextValue !== textNode.nodeValue) {
        textNode.nodeValue = nextValue;
      }
    });
  }

  function applyManualHyphenation(root) {
    getIngredientSections(root).forEach((section) => {
      section.querySelectorAll('.tfx-ingredient__excerpt, .tfx-rte').forEach(hyphenateContainer);
    });
  }

  document.addEventListener('DOMContentLoaded', () => applyManualHyphenation(document));
  document.addEventListener('shopify:section:load', (e) => applyManualHyphenation(e.target));
  document.addEventListener('shopify:block:select', (e) => applyManualHyphenation(e.target));
})();
/* END TFX PATCH */

/* BEGIN TFX PATCH: Ingredients - recompute locked heights on resize/editor events
   Rollback: remove this IIFE block
*/
(function () {
  'use strict';

  const BIND_KEY = 'tfxIngredientsResizeBound';
  const RESIZE_DEBOUNCE_MS = 140;
  const TITLE_MAX_LINES = 2;
  const TITLE_MIN_RATIO = 0.78;
  const TITLE_FONT_STEP_PX = 0.5;
  const TITLE_MIN_PX = 11;
  const SUBTITLE_MAX_LINES = 2;
  const SUBTITLE_MIN_RATIO = 0.78;
  const SUBTITLE_FONT_STEP_PX = 0.5;
  const SUBTITLE_MIN_PX = 10;
  const TYPO_MAX_ITERATIONS = 48;
  const SECTION_STEP_DOWN_CLASS = 'tfx-ingredients--cards-step-down';
  const SECTION_CLAMP_CLASS = 'tfx-ingredients--title-clamp';

  const {
    getIngredientSections,
    hasIngredientSections,
    isDesignMode,
    syncSectionLayoutState
  } = tfxIngredientsShared;

  if (!isDesignMode() && !hasIngredientSections(document)) return;

  if (window[BIND_KEY]) return;
  window[BIND_KEY] = true;

  let resizeTimer = null;
  let rafId = null;

  /* BEGIN TFX PATCH: Ingredients - fit title/subtitle text to 2 lines
     Rollback: remove this helper + call in recalcLockedHeights, then keep CSS clamp safety only.
  */
  function parsePx(value) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function getLineHeightPx(element) {
    const styles = window.getComputedStyle(element);
    const lineHeight = parsePx(styles.lineHeight);
    if (lineHeight) return lineHeight;

    const fontSize = parsePx(styles.fontSize) || 16;
    return fontSize * 1.15;
  }

  function elementExceedsMaxLines(element, maxLines) {
    return element.getBoundingClientRect().height > ((getLineHeightPx(element) * maxLines) + 0.5);
  }

  function fitElementTypography(element, options) {
    if (!element) return true;

    const computedStyles = window.getComputedStyle(element);
    if (computedStyles.display === 'none' || computedStyles.visibility === 'hidden') return true;

    element.style.removeProperty('font-size');

    const computedFontSize = parsePx(computedStyles.fontSize) || 16;
    const minFontSize = Math.max(options.minPx, Number((computedFontSize * options.minRatio).toFixed(2)));
    let currentFontSize = computedFontSize;
    let safety = 0;

    while (
      elementExceedsMaxLines(element, options.maxLines) &&
      currentFontSize > minFontSize &&
      safety < TYPO_MAX_ITERATIONS
    ) {
      currentFontSize = Math.max(minFontSize, Number((currentFontSize - options.stepPx).toFixed(2)));
      element.style.fontSize = `${currentFontSize}px`;
      safety += 1;
    }

    return !elementExceedsMaxLines(element, options.maxLines);
  }

  function fitIngredientTexts(section) {
    let allTextFit = true;

    section.querySelectorAll('.tfx-ingredient').forEach((item) => {
      const title = item.querySelector('.tfx-ingredient__title');
      const benefit = item.querySelector('.tfx-ingredient__benefit');

      const titleFits = fitElementTypography(title, {
        maxLines: TITLE_MAX_LINES,
        minRatio: TITLE_MIN_RATIO,
        stepPx: TITLE_FONT_STEP_PX,
        minPx: TITLE_MIN_PX
      });

      const subtitleFits = fitElementTypography(benefit, {
        maxLines: SUBTITLE_MAX_LINES,
        minRatio: SUBTITLE_MIN_RATIO,
        stepPx: SUBTITLE_FONT_STEP_PX,
        minPx: SUBTITLE_MIN_PX
      });

      if (!titleFits || !subtitleFits) {
        allTextFit = false;
      }
    });

    return allTextFit;
  }
  /* END TFX PATCH */

  function recalcLockedHeights(root) {
    getIngredientSections(root).forEach((section) => {
      const items = Array.from(section.querySelectorAll('.tfx-ingredient'));
      if (!items.length) return;

      const sectionLayout = syncSectionLayoutState(section);

      section.classList.remove(SECTION_STEP_DOWN_CLASS);
      section.classList.remove(SECTION_CLAMP_CLASS);

      const snapshots = items.map((item) => {
        const toggle = item.querySelector('[data-tfx-toggle]');
        const panel = item.querySelector('[data-tfx-panel]');
        const isOpen = item.classList.contains('is-open');

        if (isOpen && toggle && panel) {
          toggle.setAttribute('aria-expanded', 'false');
          panel.hidden = true;
          item.classList.remove('is-open');
        }

        item.style.removeProperty('--tfx-locked-h');

        return { item, isOpen, toggle, panel };
      });

      let textsFit = fitIngredientTexts(section);
      if (!textsFit) {
        section.classList.add(SECTION_STEP_DOWN_CLASS);
        textsFit = fitIngredientTexts(section);
      }

      if (!textsFit) {
        // Last safety net: hard clamp to 2 lines if resize paths are exhausted.
        section.classList.add(SECTION_CLAMP_CLASS);
        fitIngredientTexts(section);
      }

      if (sectionLayout === TFX_INGREDIENTS_LAYOUT_MULTI) {
        const closedHeights = snapshots
          .map(({ item }) => Math.ceil(item.getBoundingClientRect().height))
          .filter((height) => height > 0);

        if (closedHeights.length) {
          // Desktop rule: lock all cards to the tallest closed card height.
          const maxClosedHeight = Math.max(...closedHeights);

          snapshots.forEach(({ item }) => {
            item.style.setProperty('--tfx-locked-h', `${maxClosedHeight}px`);
          });
        }
      }

      snapshots.forEach(({ item, isOpen, toggle, panel }) => {
        if (!isOpen || !toggle || !panel) return;

        toggle.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
        item.classList.add('is-open');
      });
    });
  }

  function scheduleRecalc() {
    if (resizeTimer) window.clearTimeout(resizeTimer);

    resizeTimer = window.setTimeout(() => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => recalcLockedHeights(document));
    }, RESIZE_DEBOUNCE_MS);
  }

  document.addEventListener('DOMContentLoaded', () => recalcLockedHeights(document));
  window.addEventListener('resize', scheduleRecalc, { passive: true });
  window.addEventListener('orientationchange', scheduleRecalc, { passive: true });

  document.addEventListener('shopify:section:load', (e) => recalcLockedHeights(e.target));
  document.addEventListener('shopify:section:select', (e) => recalcLockedHeights(e.target));
  document.addEventListener('shopify:section:unload', scheduleRecalc);
  document.addEventListener('shopify:section:reorder', scheduleRecalc);
  document.addEventListener('shopify:block:select', (e) => recalcLockedHeights(e.target));
  document.addEventListener('shopify:block:deselect', scheduleRecalc);

  if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
    document.fonts.ready.then(scheduleRecalc);
  }
})();
/* END TFX PATCH */
